import { selectIsBusy } from '../feedback/feedbackSlice';
import { fetchDispatchQueue } from '../operatorDispatch/operatorDispatchThunks';
import { selectKitUnconfirmed } from '../operatorKits/operatorKitsSlice';
import { fetchPackingQueue } from '../operatorPacking/operatorPackingThunks';
import { selectPackingUnconfirmed } from '../operatorPacking/operatorPackingUtils';
import { selectRouteUnconfirmed } from '../operatorPicking/operatorPickingRouteSlice';
import { fetchPickingQueue } from '../operatorPicking/operatorPickingThunks';
import { fetchPutawayPending } from '../operatorPutaway/operatorPutawayThunks';
import { fetchReceivingQueue } from '../operatorReceiving/operatorReceivingThunks';
import { operatorClockTicked, selectTasksLoadedHere } from '../operatorTasks/operatorTasksSlice';
import { fetchTasks } from '../operatorTasks/operatorTasksThunks';
import { isPageHidden } from '../../services/pageVisibility';
import { OPERATOR_FLOW, OPERATOR_SCREEN } from './operatorConstants';
import { taskKeyOf } from './operatorTaskScope';
import { selectHasTaskFlows } from './operatorUtils';

/**
 * How old a list has to be to be asked again: one that came a moment ago is
 * not asked twice. Well under the clock's minute, so a screen just opened
 * waits at most a tick and a bit, and then the list is asked every tick.
 */
export const REFRESH_AFTER_MS = 20 * 1000;

/** How long the screen has to go untouched first: a list never changes under the operator's finger. */
export const STILL_AFTER_MS = 15 * 1000;

const BACKGROUND = { background: true };

/** When a list's first page came, while it is the only one loaded: a refresh replaces it whole. */
const firstPageAt = (list) => (list.page === 1 ? list.receivedAt : null);

/**
 * The lists the clock asks again, by the screen they are on: when the list
 * last came — `null` while it is not to be touched —, and how to ask it.
 */
const REFRESHES = {
  [taskKeyOf(OPERATOR_SCREEN.HOME)]: {
    receivedAt: (state) =>
      selectHasTaskFlows(state) && selectTasksLoadedHere(state) ? state.operatorTasks.receivedAt : null,
    thunk: fetchTasks,
    argOf: () => BACKGROUND,
  },
  // Orders being chosen for a route stay as they are.
  [taskKeyOf(OPERATOR_FLOW.PICKING)]: {
    receivedAt: (state) =>
      state.operatorPickingRoute.selection.length === 0 ? firstPageAt(state.operatorPicking.queue) : null,
    thunk: fetchPickingQueue,
    argOf: () => ({ page: 1, ...BACKGROUND }),
  },
  [taskKeyOf(OPERATOR_FLOW.PACKING)]: {
    receivedAt: (state) => firstPageAt(state.operatorPacking.queue),
    thunk: fetchPackingQueue,
    argOf: () => ({ page: 1, ...BACKGROUND }),
  },
  [taskKeyOf(OPERATOR_FLOW.RECEIVING)]: {
    receivedAt: (state) => firstPageAt(state.operatorReceiving.queue),
    thunk: fetchReceivingQueue,
    argOf: () => ({ page: 1, ...BACKGROUND }),
  },
  // Only the list: not while a row is being stored.
  [taskKeyOf(OPERATOR_FLOW.PUTAWAY)]: {
    receivedAt: ({ operatorPutaway: putaway }) =>
      putaway.selectedKey === null && !putaway.requested ? firstPageAt(putaway.list) : null,
    thunk: fetchPutawayPending,
    argOf: () => ({ page: 1, ...BACKGROUND }),
  },
  // Only while the load is empty: the queue beside a load being put up is the operator's to ask.
  [taskKeyOf(OPERATOR_FLOW.DISPATCH)]: {
    receivedAt: ({ operatorDispatch: station }) =>
      station.load.orderIds.length === 0 ? firstPageAt(station.queue) : null,
    thunk: fetchDispatchQueue,
    argOf: (state) => ({ courier: state.operatorDispatch.queue.courier, page: 1, ...BACKGROUND }),
  },
};

/**
 * Whether the operator is away from the screen's controls: nothing open over
 * it, on its way or waiting to be sent, no touch for `STILL_AFTER_MS`, and
 * the page in sight.
 *
 * @param {object} state
 * @param {number} now
 */
const isStill = (state, now) => {
  const { sheets, held, signal, lastTouchAt } = state.operatorDevice;
  if (sheets > 0 || held || signal?.retry) return false;
  if (lastTouchAt != null && now - lastTouchAt < STILL_AFTER_MS) return false;
  if (selectIsBusy(state)) return false;
  if (selectPackingUnconfirmed(state) || selectRouteUnconfirmed(state) || selectKitUnconfirmed(state)) return false;
  return !isPageHidden();
};

/**
 * What the clock's tick asks again, `{thunk, arg}`, or `null`. Only a list on
 * screen — Inicio's tray, or a station's queue —, never a task: one that came
 * at least once, is older than `REFRESH_AFTER_MS` and that the operator is not
 * working on.
 *
 * @param {object} state
 * @param {number} now - The phone's time, as the tick read it.
 */
export const backgroundRefreshFor = (state, now) => {
  const refresh = REFRESHES[state.operatorDevice.task?.key];
  if (!refresh) return null;
  const receivedAt = refresh.receivedAt(state);
  if (receivedAt == null || now - receivedAt < REFRESH_AFTER_MS || !isStill(state, now)) return null;
  return { thunk: refresh.thunk, arg: refresh.argOf(state) };
};

/**
 * The lists on screen, asked again in the background on the local clock's
 * tick, once a minute: no timer of its own, nothing while the page is hidden,
 * and nothing on screen says it is happening until the list changes.
 *
 * @param {Function} startListening
 */
export const registerOperatorRefreshListeners = (startListening) => {
  startListening({
    actionCreator: operatorClockTicked,
    effect: (action, { dispatch, getState }) => {
      const refresh = backgroundRefreshFor(getState(), action.payload);
      if (refresh) dispatch(refresh.thunk(refresh.arg));
    },
  });
};
